import { QRScannerStatus } from "@/types/login/QRScanner";
import type {
  IUseQRScanner,
  IUseQRScannerParams,
} from "@/types/login/QRScanner";
import { useCallback, useEffect, useRef, useState } from "react";

type JsQrDecoder = typeof import("jsqr").default;

const cameraConstraints: MediaStreamConstraints = {
  video: {
    facingMode: { ideal: "environment" },
    width: { ideal: 640 },
    height: { ideal: 480 },
  },
};

const errorMessageFor = (error: unknown): string => {
  const name =
    error instanceof Error || error instanceof DOMException ? error.name : "";
  if (name === "NotAllowedError") {
    return "Camera access was blocked. Allow it in your browser settings, or type the code instead.";
  }
  if (name === "NotFoundError") {
    return "No camera found. Type the code instead.";
  }
  return "Could not start the camera. Type the code instead.";
};

/**
 * Camera-based QR scanning. Acquires the rear camera, decodes each frame with
 * jsQR, and calls onDetected once with the first non-empty decoded string.
 * Releases the camera on unmount and via the returned stop().
 */
const useQRScanner = ({
  active,
  onDetected,
}: IUseQRScannerParams): IUseQRScanner => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const frameRef = useRef<number | null>(null);
  const detectedRef = useRef(false);
  const decoderRef = useRef<JsQrDecoder | null>(null);
  const onDetectedRef = useRef(onDetected);
  const [status, setStatus] = useState<QRScannerStatus>(
    QRScannerStatus.Requesting,
  );
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    onDetectedRef.current = onDetected;
  }, [onDetected]);

  const stop = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        track.stop();
      }
      streamRef.current = null;
    }
  }, []);

  const scan = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const decode = decoderRef.current;
    if (!video || !canvas || !decode || detectedRef.current) {
      return;
    }
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      const context = canvas.getContext("2d");
      if (context) {
        if (canvas.width !== video.videoWidth) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
        }
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = context.getImageData(
          0,
          0,
          canvas.width,
          canvas.height,
        );
        const result = decode(
          imageData.data,
          imageData.width,
          imageData.height,
          {
            inversionAttempts: "dontInvert",
          },
        );
        const decoded = result?.data.trim();
        if (decoded) {
          detectedRef.current = true;
          stop();
          onDetectedRef.current(decoded);
          return;
        }
      }
    }
    frameRef.current = requestAnimationFrame(scan);
  }, [stop]);

  useEffect(() => {
    if (!active) {
      return undefined;
    }
    let cancelled = false;
    detectedRef.current = false;
    setStatus(QRScannerStatus.Requesting);
    setErrorMessage("");

    const startCamera = async () => {
      if (streamRef.current) {
        return;
      }
      try {
        if (!decoderRef.current) {
          decoderRef.current = (await import("jsqr")).default;
        }
        if (cancelled) {
          return;
        }
        const stream =
          await navigator.mediaDevices.getUserMedia(cameraConstraints);
        const video = videoRef.current;
        if (cancelled || !video || detectedRef.current) {
          for (const track of stream.getTracks()) {
            track.stop();
          }
          return;
        }
        streamRef.current = stream;
        video.srcObject = stream;
        video.setAttribute("playsinline", "true");
        await video.play();
        if (cancelled) {
          stop();
          return;
        }
        setStatus(QRScannerStatus.Scanning);
        frameRef.current = requestAnimationFrame(scan);
      } catch (error) {
        if (cancelled) {
          return;
        }
        setErrorMessage(errorMessageFor(error));
        setStatus(QRScannerStatus.Error);
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      stop();
    };
  }, [active, scan, stop]);

  return { videoRef, canvasRef, status, errorMessage, stop };
};

export default useQRScanner;
